<template>
  <NuxtLayout :name="layoutName" :navbar-color="layoutNavbarColor">
    <div v-if="story">
      <section class="container categories-page__container">
        <BlogBreadcrumbs class="categories-page__breadcrumbs" />
        <div class="categories-page">
          <h1 class="categories-page__title">Category: {{ story.name }}</h1>
          <section class="categories-page__grid">
            <BlogArticlePreview
              v-for="article in loadedElements"
              :key="article.uuid"
              :article="article"
            />
          </section>
        </div>
        <div
          v-if="!hideLoadButton"
          ref="loaderEl"
          class="categories-page__load-section"
        >
          <LoaderSpinner v-if="isLoading && !isError" blue />
          <p v-if="isError" class="categories-page__error">
            {{ $t('common.something_went_wrong') }}
          </p>
        </div>
      </section>
      <section class="categories-page__newsletter-section">
        <div class="container">
          <BlogNewsletterCard />
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
const { slug } = useRoute().params
const { story } = await useFetchStory(`blog/categories/${slug}`)

const loaderEl = ref(null)

const queryParams = {
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors, sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 12,
  filter_query: {
    categories: {
      in_array: story.value.uuid,
    },
  },
}

const {
  initObserver,
  isLoading,
  hideLoadButton,
  loadedElements,
  loadFirstPage,
  isError,
} = useLoadMore(queryParams)

loadFirstPage()

if (story.value.content.component === 'sb-page') {
  throw new Error('Unexpected story content type')
}

onMounted(() => {
  initObserver(loaderEl.value)
})

const metaTitle = `${story.value.name} - blog - Infermedica`

const { getBreadcrumbsJsonLd } = useBreadcrumbs()

useCustomHead(story, {
  title: metaTitle,
  metaArgs: [
    {
      hid: 'title',
      name: 'title',
      content: metaTitle,
    },
    {
      hid: 'og:title',
      name: 'og:title',
      content: metaTitle,
    },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: metaTitle,
    },
  ],
  jsonLd: getBreadcrumbsJsonLd(),
})
watch(loaderEl, () => {
  initObserver(loaderEl.value)
})

useCustomHead(story)
defineI18nRoute({
  locales: ['en'],
})

const { layoutName, layoutNavbarColor } = useStoryLayout<any>(story)
</script>

<style lang="scss" scoped>
.categories-page {
  &__container {
    margin-bottom: 120px;
  }

  &__card {
    display: block;
    max-width: 660px;
  }

  &__grid {
    padding-top: 56px;
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 48px;
    margin-bottom: 56px;

    @media (min-width: $breakpoint-lg) {
      row-gap: 56px;
      column-gap: 56px;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 96px;
    }
  }

  &__load-section {
    min-height: 20px;
    width: 100%;
    margin: 40px auto;
    margin: 20px auto 0 auto;
    text-align: center;
  }

  &__newsletter-section {
    background-color: var(--paper-100);
    padding: 80px 0;

    @media (min-width: $breakpoint-md) {
      padding: 128px 0;
    }
  }

  &__error {
    padding-top: 40px;
    display: block;
    font-weight: var(--font-weight-paragraph-bold);
  }
}
</style>
